/* 1차 선별 결과(screening_results.csv)로 폴리곤 작업 목록을 만드는 함수 모음.
 * 선별 결과 CSV 는 서버 vision_dataset/v1/screening/ 에 있으며, 불량(DEFECT)·애매(UNSURE)로
 * 판정된 이미지만 폴리곤 작업 대상이다. 화면(LabelingPage)과 분리해 두어 따로 테스트할 수 있게 한다. */

// 작업 목록을 만들 때 꼭 있어야 하는 CSV 열
export const REQUIRED_SCREENING_COLUMN_LIST = ['anno_image_id', 'relative_path', 'screening_result', 'defect_type_list'];
// 폴리곤 작업 대상이 되는 선별 판정
export const WORK_TARGET_SCREENING_RESULT_LIST = ['DEFECT', 'UNSURE'];
// 선별 CSV 의 불량 유형 구분자 (예: FLASH|WELDLINE)
const DEFECT_TYPE_SEPARATOR = '|';
// 유형 필터 선택지. ALL=전체, UNSURE=애매, 그 외는 불량 유형 코드
export const WORK_QUEUE_TYPE_FILTER_OPTION_LIST = [
  { value: 'ALL', label: '전체' },
  { value: 'FLASH', label: '버(플래시)' },
  { value: 'WELDLINE', label: '웰드라인' },
  { value: 'SHORT', label: '미성형' },
  { value: 'SINK', label: '수축' },
  { value: 'OTHER', label: '기타' },
  { value: 'UNSURE', label: '애매' },
];

/* 문자열 맨 앞의 UTF-8 BOM 을 없앤다. 엑셀로 저장한 CSV 는 첫 열 이름 앞에 BOM 이 붙는다. */
function removeByteOrderMark(text) {
  return text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
}

/* CSV 텍스트를 행(문자열 배열)의 배열로 나눈다.
 * 큰따옴표로 감싼 칸 안의 쉼표·줄바꿈과 "" (따옴표 이스케이프), CRLF 줄바꿈을 처리한다. */
export function splitCsvTextIntoRowList(csvText) {
  const rowList = [];
  let currentRow = [];
  let currentField = '';
  let isInsideQuotes = false;
  const text = removeByteOrderMark(csvText);
  for (let characterIndex = 0; characterIndex < text.length; characterIndex += 1) {
    const character = text[characterIndex];
    if (isInsideQuotes) {
      if (character === '"' && text[characterIndex + 1] === '"') { currentField += '"'; characterIndex += 1; }
      else if (character === '"') isInsideQuotes = false;
      else currentField += character;
    } else if (character === '"') {
      isInsideQuotes = true;
    } else if (character === ',') {
      currentRow.push(currentField); currentField = '';
    } else if (character === '\n' || character === '\r') {
      // CRLF 는 한 번의 줄바꿈으로 본다
      if (character === '\r' && text[characterIndex + 1] === '\n') characterIndex += 1;
      currentRow.push(currentField); currentField = '';
      rowList.push(currentRow); currentRow = [];
    } else {
      currentField += character;
    }
  }
  // 마지막 줄에 줄바꿈이 없는 경우
  if (currentField !== '' || currentRow.length > 0) { currentRow.push(currentField); rowList.push(currentRow); }
  // 빈 줄은 버린다
  return rowList.filter(row => !(row.length === 1 && row[0].trim() === ''));
}

/* CSV 텍스트를 {열이름: 값} 객체 배열로 바꾼다. 필수 열이 없으면 어떤 열이 없는지 알려주는 에러를 던진다. */
export function parseScreeningCsvText(csvText) {
  const rowList = splitCsvTextIntoRowList(csvText);
  if (rowList.length === 0) throw new Error('CSV 파일이 비어 있습니다');
  const headerList = rowList[0].map(header => header.trim());
  const missingColumnList = REQUIRED_SCREENING_COLUMN_LIST.filter(column => !headerList.includes(column));
  if (missingColumnList.length > 0) {
    throw new Error(`선별 결과 CSV 가 아닙니다 (없는 열: ${missingColumnList.join(', ')})`);
  }
  return rowList.slice(1).map(row => Object.fromEntries(headerList.map((header, columnIndex) => [header, row[columnIndex] ?? ''])));
}

/* "FLASH|WELDLINE" 같은 불량 유형 문자열을 코드 배열로 바꾼다(빈 값·공백 제거). */
function splitDefectTypeText(defectTypeText) {
  return (defectTypeText || '').split(DEFECT_TYPE_SEPARATOR).map(defectType => defectType.trim()).filter(Boolean);
}

/* 선별 CSV 행 배열에서 폴리곤 작업 목록을 만든다.
 * 불량·애매만 남기고, 같은 이미지가 여러 번 나오면 뒤의 행(재확인 결과)을 쓰며, 경로 순으로 정렬한다. */
export function buildScreeningWorkQueue(screeningRowList) {
  const workItemByImageId = new Map();
  screeningRowList.forEach(screeningRow => {
    const annoImageId = Number(screeningRow.anno_image_id);
    if (!Number.isInteger(annoImageId) || annoImageId <= 0) return;
    const screeningResult = (screeningRow.screening_result || '').trim();
    if (!WORK_TARGET_SCREENING_RESULT_LIST.includes(screeningResult)) {
      // 뒤에서 정상 등으로 바뀐 이미지는 작업 목록에서 뺀다
      workItemByImageId.delete(annoImageId);
      return;
    }
    workItemByImageId.set(annoImageId, {
      annoImageId,
      relativePath: screeningRow.relative_path,
      screeningResult,
      defectTypeList: splitDefectTypeText(screeningRow.defect_type_list),
    });
  });
  return [...workItemByImageId.values()].sort((left, right) => left.relativePath.localeCompare(right.relativePath));
}

/* 작업 항목이 유형 필터에 맞는지 확인한다. */
function isWorkItemMatchingTypeFilter(workItem, typeFilter) {
  if (typeFilter === 'ALL') return true;
  if (typeFilter === 'UNSURE') return workItem.screeningResult === 'UNSURE';
  return workItem.defectTypeList.includes(typeFilter);
}

/* 작업 목록을 유형 필터로 거른다. */
export function filterWorkQueueByType(workQueue, typeFilter) {
  return workQueue.filter(workItem => isWorkItemMatchingTypeFilter(workItem, typeFilter));
}

/* 유형 필터 선택지마다 해당하는 작업 수를 센다(선택지 옆 숫자 표시용). */
export function countWorkQueueByTypeFilter(workQueue) {
  return Object.fromEntries(WORK_QUEUE_TYPE_FILTER_OPTION_LIST.map(option => [
    option.value, filterWorkQueueByType(workQueue, option.value).length,
  ]));
}

/* 이미지 상태 정보로 아직 작업이 남았는지 판단한다.
 * 대기(pending)이면서 판정불가로 저장하지 않은 이미지만 남은 작업이다. 상태를 모르면 남은 것으로 본다. */
export function isWorkItemRemaining(imageStatusInfo) {
  if (!imageStatusInfo) return true;
  return imageStatusInfo.status === 'pending' && imageStatusInfo.image_class !== 'UNDECIDABLE';
}

/* 현재 이미지 다음의 남은 작업을 찾는다. 뒤쪽에 없으면 앞쪽에서 찾고, 현재 이미지는 고르지 않는다. */
export function findNextRemainingWorkItem(workQueue, currentImageId, imageStatusInfoById) {
  const currentIndex = workQueue.findIndex(workItem => workItem.annoImageId === currentImageId);
  const orderedWorkQueue = [...workQueue.slice(currentIndex + 1), ...workQueue.slice(0, Math.max(currentIndex, 0))];
  return orderedWorkQueue.find(workItem =>
    workItem.annoImageId !== currentImageId && isWorkItemRemaining(imageStatusInfoById[workItem.annoImageId])) || null;
}

/* 작업 목록에 들어 있는 폴더 이름 목록(중복 제거)을 만든다. 상태를 폴더 단위로 조회할 때 쓴다. */
export function collectWorkQueueFolderNameList(workQueue) {
  return [...new Set(workQueue.map(workItem => workItem.relativePath.split('/')[0]).filter(Boolean))];
}

/* 선별 때 고른 유형 중 라벨링 클래스에 있는 첫 유형을 돌려준다(그릴 결함 유형 미리 선택용). 없으면 null. */
export function selectDrawClassForWorkItem(workItem, classCodeList) {
  if (!workItem) return null;
  return workItem.defectTypeList.find(defectType => classCodeList.includes(defectType)) || null;
}
